import type {
  ApprovalRiskThresholdV1,
  BusinessGoalV1,
  CampaignAiPolicyOverrideV1,
  CampaignAiSetupResponseV1,
  ExecutionModeV1,
} from "@adpilot/shared";
import { resolveExecutionModePolicyUpdateV1, resolveExecutionModeV1 } from "@adpilot/shared";
import { IconCheck, IconShieldCheck, IconX } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ControlButton } from "@/components/patterns/control-button";
import { UnitInput } from "@/components/patterns/UnitInput";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { getApiErrorMessage } from "@/lib/api-error";
import { fetchCampaignAiSetup, updateCampaignAiSetup } from "@/lib/campaign-api";
import { cn } from "@/lib/utils";

type PolicyMode = "inherit" | "override" | "disabled";

// Goal labels/descriptions resolve at render via `t("policyDialog.goals.<value>.*")`;
// the module-scope list only carries the display order.
const GOAL_VALUES: readonly BusinessGoalV1[] = [
  "improve_efficiency",
  "grow_sales",
  "explore_and_learn",
  "defend_branded_traffic",
  "launch_product",
  "clear_inventory",
];

interface CampaignPolicyDialogProps {
  campaignId: string | null;
  campaignName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CampaignPolicyDialog({
  campaignId,
  campaignName,
  open,
  onOpenChange,
}: CampaignPolicyDialogProps) {
  const { t } = useTranslation("campaign");
  const queryClient = useQueryClient();
  const [policyMode, setPolicyMode] = useState<PolicyMode>("inherit");
  const [goal, setGoal] = useState<BusinessGoalV1>("improve_efficiency");
  const [targetAcos, setTargetAcos] = useState("30");
  const [executionMode, setExecutionMode] = useState<ExecutionModeV1>("recommendation");
  const [executionModeDirty, setExecutionModeDirty] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const query = useQuery({
    queryKey: ["campaign", campaignId, "ai-policy"],
    queryFn: () => fetchCampaignAiSetup(campaignId!),
    enabled: open && campaignId !== null,
  });

  useEffect(() => {
    if (!query.data) return;
    const overrides = query.data.raw.campaignOverrides;
    const hasOverride = Object.values(overrides).some((leaf) => leaf.mode === "override");
    setPolicyMode(
      !query.data.raw.campaignEnabled ? "disabled" : hasOverride ? "override" : "inherit",
    );
    setGoal(query.data.effective.businessGoal);
    setTargetAcos(String(Math.round(query.data.effective.targetAcos * 100)));
    setExecutionMode(resolveExecutionModeV1(query.data.effective));
    setExecutionModeDirty(false);
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!campaignId || !query.data) throw new Error("Campaign policy unavailable");
      const inherited: CampaignAiPolicyOverrideV1 = {
        businessGoal: { mode: "follow_system" },
        targetAcos: { mode: "follow_system" },
        approvalRiskThreshold: { mode: "follow_system" },
        riskPreference: { mode: "follow_system" },
        bidChangeRiskThresholds: { mode: "follow_system" },
        dryRun: { mode: "follow_system" },
      };
      if (policyMode !== "override") {
        return updateCampaignAiSetup(campaignId, {
          version: 1,
          campaignEnabled: policyMode === "inherit",
          overrides: inherited,
        });
      }
      const executionFields = resolveExecutionModePolicyUpdateV1(
        query.data.effective,
        executionMode,
        executionModeDirty,
      );
      return updateCampaignAiSetup(campaignId, {
        version: 1,
        campaignEnabled: true,
        overrides: {
          ...inherited,
          businessGoal: { mode: "override", value: goal },
          targetAcos: { mode: "override", value: Number(targetAcos) / 100 },
          approvalRiskThreshold: {
            mode: "override",
            value: executionFields.approvalRiskThreshold,
          },
          dryRun: { mode: "override", value: executionFields.dryRun },
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
      toast.success(t("policyDialog.saved"));
      setConfirmDisable(false);
      onOpenChange(false);
    },
    onError: async (error) => toast.error(await getApiErrorMessage(error)),
  });

  const validAcos =
    Number.isFinite(Number(targetAcos)) && Number(targetAcos) >= 1 && Number(targetAcos) <= 200;
  const shopTarget = query.data ? Math.round(query.data.systemPolicy.targetAcos * 100) : 24;

  function savePolicy(): void {
    if (policyMode === "disabled" && query.data?.raw.campaignEnabled) {
      setConfirmDisable(true);
      return;
    }
    mutation.mutate();
  }

  function selectExecutionMode(nextMode: ExecutionModeV1): void {
    if (nextMode !== executionMode) setExecutionModeDirty(true);
    setExecutionMode(nextMode);
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogPopup
          className="h-[680px] max-w-[600px] overflow-hidden rounded-[14px]"
          showCloseButton={false}
        >
          <DialogClose
            aria-label={t("common:actions.close")}
            className="absolute right-[22px] top-5 z-10"
            render={
              <Button
                size="icon-sm"
                variant="outline"
                className="size-[30px] rounded-[8px] focus-visible:ring-0"
              />
            }
          >
            <IconX className="size-3.5" />
          </DialogClose>
          <DialogHeader className="h-20 shrink-0 justify-center gap-0.5 border-b border-line px-[22px] py-0 pr-16">
            <DialogTitle className="text-[16px] leading-[22px]">
              {t("policyDialog.title")}
            </DialogTitle>
            <DialogDescription className="truncate text-[11px] leading-4">
              {campaignName ?? t("policyDialog.campaignFallback")} ·{" "}
              {policyMode === "override"
                ? t("policyDialog.overrideShopPolicy")
                : t("policyDialog.choosePolicySource")}
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="min-h-0 flex-1 px-[22px] py-3" scrollFade={false}>
            {query.isLoading || !query.data ? (
              <Skeleton className="h-[500px] w-full rounded-[10px]" />
            ) : (
              <div className="flex flex-col gap-[13px]">
                <section>
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-[11px] font-semibold text-ink">
                      {t("policyDialog.policySource")}
                    </h3>
                    <span className="text-[9px] text-muted-foreground">
                      {t("policyDialog.currentSource", {
                        label: currentPolicySourceLabel(query.data.raw, t),
                      })}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <PolicySourceCard
                      selected={policyMode === "inherit"}
                      title={t("policyDialog.source.inheritTitle")}
                      description={t("policyDialog.source.inheritDescription", {
                        target: shopTarget,
                        mode: executionModeLabel(
                          query.data.systemPolicy.dryRun,
                          query.data.systemPolicy.approvalRiskThreshold,
                          t,
                        ),
                      })}
                      onClick={() => setPolicyMode("inherit")}
                    />
                    <PolicySourceCard
                      selected={policyMode === "override"}
                      title={t("policyDialog.source.overrideTitle")}
                      description={t("policyDialog.source.overrideDescription")}
                      onClick={() => setPolicyMode("override")}
                    />
                    <PolicySourceCard
                      selected={policyMode === "disabled"}
                      title={t("policyDialog.source.disableTitle")}
                      description={t("policyDialog.source.disableDescription")}
                      onClick={() => setPolicyMode("disabled")}
                    />
                  </div>
                </section>

                {policyMode === "override" ? (
                  <>
                    <section>
                      <div className="mb-2 flex items-center justify-between">
                        <h3 className="text-[11px] font-semibold text-ink">
                          {t("detail.policy.businessGoal")}
                        </h3>
                        <span className="text-[9px] text-muted-foreground">
                          {t("policyDialog.chooseOne")}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {GOAL_VALUES.map((value) => (
                          <GoalCard
                            key={value}
                            selected={goal === value}
                            title={t(`policyDialog.goals.${value}.label`)}
                            description={t(`policyDialog.goals.${value}.description`)}
                            onClick={() => setGoal(value)}
                          />
                        ))}
                      </div>
                    </section>
                    <section>
                      <h3 className="mb-2 text-[12px] font-semibold leading-4 text-ink">
                        {t("detail.policy.targetAcos")}
                      </h3>
                      <div className="flex h-[34px] items-center gap-2.5">
                        <UnitInput
                          aria-invalid={!validAcos}
                          aria-label={t("detail.policy.targetAcos")}
                          value={targetAcos}
                          onChange={(event) => setTargetAcos(event.target.value)}
                          className="w-[130px] shrink-0"
                          inputMode="decimal"
                          unit="%"
                        />
                        <span className="flex h-7 items-center gap-1.5 rounded-full border border-green/15 bg-green/5 px-2.5 text-[10px] font-medium leading-[13px] text-green-11">
                          <IconCheck className="size-3" />{" "}
                          {t("policyDialog.shopDefault", { target: shopTarget })}
                        </span>
                        <span className="text-[10px] leading-[14px] text-muted-foreground">
                          {t("policyDialog.campaignSpecificTarget")}
                        </span>
                      </div>
                    </section>
                    <section>
                      <div className="mb-2 flex items-center justify-between">
                        <h3 className="text-[11px] font-semibold text-ink">
                          {t("policyDialog.executionModeHeading")}
                        </h3>
                        <span className="text-[9px] text-muted-foreground">
                          {t("policyDialog.executionModeHint")}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <ExecutionCard
                          selected={executionMode === "recommendation"}
                          title={t("detail.executionMode.recommendation")}
                          description={t("policyDialog.execution.recommendationDescription")}
                          onClick={() => selectExecutionMode("recommendation")}
                        />
                        <ExecutionCard
                          selected={executionMode === "semi-auto"}
                          title={t("detail.executionMode.semiAuto")}
                          badge={t("policyDialog.recommendedBadge")}
                          description={t("policyDialog.execution.semiAutoDescription")}
                          onClick={() => selectExecutionMode("semi-auto")}
                        />
                        <ExecutionCard
                          selected={executionMode === "full-auto"}
                          title={t("detail.executionMode.fullAuto")}
                          description={t("policyDialog.execution.fullAutoDescription")}
                          onClick={() => selectExecutionMode("full-auto")}
                        />
                      </div>
                    </section>
                  </>
                ) : (
                  <div className="flex min-h-[286px] items-center justify-center rounded-[10px] border border-dashed border-line bg-subtle/40 px-12 text-center text-[11px] leading-5 text-muted-foreground">
                    {policyMode === "inherit"
                      ? t("policyDialog.inheritPlaceholder")
                      : t("policyDialog.disabledPlaceholder")}
                  </div>
                )}

                <div className="flex h-[52px] shrink-0 items-start gap-3 rounded-[9px] border border-line bg-subtle px-3 py-2.5">
                  <IconShieldCheck className="mt-0.5 size-3.5 shrink-0 text-ink" stroke={1.8} />
                  <div>
                    <p className="text-[10px] font-semibold leading-4 text-ink">
                      {t("policyDialog.guardrailsTitle")}
                    </p>
                    <p className="text-[9px] leading-4 text-muted-foreground">
                      {t("policyDialog.guardrailsDescription")}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </DialogPanel>
          <DialogFooter className="h-[62px] shrink-0 items-center justify-between bg-surface px-[22px] py-0">
            <p className="mr-auto text-[9px] text-muted-foreground">
              {t("policyDialog.changesApply")}
            </p>
            <ControlButton
              controlSize="toolbar"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("common:actions.cancel")}
            </ControlButton>
            <ControlButton
              controlSize="toolbar"
              loading={mutation.isPending}
              disabled={query.isLoading || (policyMode === "override" && !validAcos)}
              onClick={savePolicy}
            >
              {t("common:actions.save")}
            </ControlButton>
          </DialogFooter>
        </DialogPopup>
      </Dialog>
      <AlertDialog open={confirmDisable} onOpenChange={setConfirmDisable}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <div className="bg-warning/10 text-warning-foreground">
              <IconShieldCheck />
            </div>
            <AlertDialogTitle>{t("policyDialog.disableConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("policyDialog.disableConfirmDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose
              render={<Button variant="outline">{t("policyDialog.keepEnabled")}</Button>}
            />
            <Button
              variant="destructive"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            >
              {t("header.disableAi")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
}

function PolicySourceCard({ selected, title, description, onClick }: ChoiceProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cn(
        "relative flex h-[46px] flex-col justify-center rounded-[8px] border px-2.5 text-left",
        selected ? "border-[1.5px] border-ink" : "border-line hover:bg-subtle/60",
      )}
      onClick={onClick}
    >
      {selected ? (
        <span className="absolute right-2 top-2 flex size-3.5 items-center justify-center rounded-full bg-ink text-white">
          <IconCheck className="size-2.5" />
        </span>
      ) : null}
      <span className="text-[10px] font-semibold leading-4 text-ink">{title}</span>
      <span className="text-[8px] leading-3 text-muted-foreground">{description}</span>
    </button>
  );
}

interface ChoiceProps {
  selected: boolean;
  title: string;
  description: string;
  onClick: () => void;
}

function GoalCard({ selected, title, description, onClick }: ChoiceProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cn(
        "flex h-[58px] gap-2 rounded-[9px] border px-2.5 py-2 text-left",
        selected ? "border-[1.5px] border-ink" : "border-line hover:bg-subtle/60",
      )}
      onClick={onClick}
    >
      <RadioDot selected={selected} />
      <span className="min-w-0">
        <span className="block text-[11px] font-semibold leading-4 text-ink">{title}</span>
        <span className="block text-[9px] leading-4 text-muted-foreground">{description}</span>
      </span>
    </button>
  );
}

function ExecutionCard({
  selected,
  title,
  description,
  badge,
  onClick,
}: ChoiceProps & { badge?: string }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cn(
        "relative flex h-14 flex-col justify-center rounded-[9px] border px-2.5 text-left",
        selected ? "border-[1.5px] border-ink" : "border-line hover:bg-subtle/60",
      )}
      onClick={onClick}
    >
      <span className="flex items-center justify-between text-[10px] font-semibold text-ink">
        <span>{title}</span>
        {badge ? (
          <span className="text-[8px] font-normal text-muted-foreground">{badge}</span>
        ) : null}
      </span>
      <span className="mt-0.5 text-[9px] text-muted-foreground">{description}</span>
    </button>
  );
}

function RadioDot({ selected }: { selected: boolean }) {
  return (
    <span
      className={cn(
        "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border",
        selected ? "border-ink" : "border-line",
      )}
    >
      {selected ? <span className="size-2 rounded-full bg-ink" /> : null}
    </span>
  );
}

function currentPolicySourceLabel(
  raw: CampaignAiSetupResponseV1["raw"],
  t: (key: string) => string,
): string {
  if (!raw.campaignEnabled) return t("policyDialog.sourceLabel.disabled");
  return Object.values(raw.campaignOverrides).some((leaf) => leaf.mode === "override")
    ? t("policyDialog.sourceLabel.campaignOverride")
    : t("policyDialog.sourceLabel.inherited");
}

function executionModeLabel(
  dryRun: boolean,
  threshold: ApprovalRiskThresholdV1,
  t: (key: string) => string,
): string {
  const mode = resolveExecutionModeV1({ dryRun, approvalRiskThreshold: threshold });
  if (mode === "recommendation") return t("detail.executionMode.recommendation");
  return mode === "full-auto"
    ? t("detail.executionMode.fullAuto")
    : t("detail.executionMode.semiAuto");
}
